// Modules
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { getLang } from 'redux-pagan';
import { scroller } from 'react-scroll';
import browserHistory from 'react-router/lib/browserHistory';
import cx from 'classnames';
import get from 'lodash/get';
// Components
import MessageSection from '../../components/ContactUsForm/MessageSection';
import UserDetailsSection from '../../components/ContactUsForm/UserDetailsSection';
// Redux
import { sendContactUsForm } from '../../redux/modules/contactUs';
import { hideMessageModal, openMessageModal } from '../../redux/modules/messageModal';
// Helper
import scrollToUtil from '../../utils/scrollTo';
import appendLocale from '../../helpers/appendLocale';
import { trackEvent } from '../../utils/analytics/analyticsManager';
import { eventCategory, eventAction, eventLabel } from '../../utils/analytics/analyticsConstants';
import { contactUsUsage, ourContacts } from '../../constants';
import { emailRegexValidation } from '../../utils/validate';
// Styles
import * as styles from './ContactUsFormContainer.scss';

scroller.scrollTo = scrollToUtil.bind(scroller);
const requiredFields = usage => ((usage === contactUsUsage.agent || usage === contactUsUsage.realtorPackages)
  ? ['name', 'email', 'phone', 'captchaToken', 'companyName']
  : ['name', 'email', 'subject', 'message', 'captchaToken']
);

class ContactUsFormContainer extends Component {
  state = {
    formData: {},
    invalidFields: [],
    showPreloader: false
  };

  componentWillReceiveProps = ({ contactUs, isMessageModalOpen }) => {
    const stateModifier = {};

    if (contactUs.loading !== this.props.contactUs.loading) {
      stateModifier.showPreloader = contactUs.loading;
    }

    if (contactUs.loaded && contactUs.loaded !== this.props.contactUs.loaded) {
      this.props.openMessageModal({
        message: (
          <div className="contentMessage">
            <div>
              <p>{this.props.translate('contactUsPage', 'formSent')}</p>
              <button
                className="btn btn-primary"
                onClick={this.handleModalsClose}
              >
                {this.props.translate('contactUsPage', 'ok')}
              </button>
            </div>
          </div>
        ),
        isCloseButtonInvisible: true
      });
    }

    if (contactUs.error && contactUs.error !== this.props.contactUs.error) {
      this.props.openMessageModal({
        message: (
          <div className="contentMessage">
            <div>
              <p>{this.props.translate('contactUsPage', 'error')}</p>
              <button
                className="btn btn-primary"
                onClick={this.handleModalsClose}
              >
                {this.props.translate('contactUsPage', 'ok')}
              </button>
            </div>
          </div>
        )
      });
    }

    if (!isMessageModalOpen && contactUs.loaded && this.props.isMessageModalOpen) {
      this.contactUsForm.reset();
      if (!this.props.isNative) {
        if (this.props.usage === contactUsUsage.agent || this.props.usage === contactUsUsage.realtorPackages) {
          browserHistory.push(appendLocale(this.props.locale, '/'));
        } else if (this.props.usage === contactUsUsage.modal) {
          this.props.hideContactUsModal();
          return;
        } else {
          browserHistory.push(appendLocale(this.props.locale, '/'));
        }
      }
    }

    if (Object.keys(stateModifier).length) {
      this.setState(stateModifier);
    }
  };

  mdFiles = __CLIENT__ ? require.context('../../translations/publishContactUs/') : () => { };

  handleModalsClose = () => {
    this.props.hideMessageModal();
    if (this.props.isContactUsModalOpen) {
      this.props.hideContactUsModal();
    }
  };

  handleChange = (evt) => {
    const { name, value } = evt.target;
    this.saveFormData(name, value);
  };

  fetchReCaptchaToken = (token) => {
    this.saveFormData('captchaToken', token);
  };

  saveFormData = (name, value) => {
    const invalidFields = [...this.state.invalidFields];
    const index = invalidFields.indexOf(name);
    const formData = {
      ...this.state.formData,
      [name]: value
    };

    if (index !== -1 && this.validateField(name, formData)) {
      invalidFields.splice(index, 1);
    }

    this.setState({
      invalidFields,
      formData
    });
  };

  handleSubmit = (evt) => {
    evt.preventDefault();
    const invalidFields = this.findInvalidRequiredFields();
    this.setState({ invalidFields });
    if (!invalidFields.length) {
      this.sendFormData(this.state.formData);
    } else if (invalidFields.length === 1 && invalidFields[0] === 'captchaToken') {
      return true; // Request captcha
    } else {
      this.scrollToInvalidField(invalidFields[0]);
    }
    return undefined;
  };

  sendFormData = ({
    name,
    message,
    email,
    phone,
    subject: title,
    captchaToken: captcha,
    companyName,
    package_name
  }) => {
    const params = {
      name,
      title,
      message,
      captcha,
      email: email.toLowerCase(),
      ...(phone) ? { phone } : {},
      ...(this.props.userData && this.props.userData.id) ? { user_id: this.props.userData.id } : {},
      ...companyName && { company_name: companyName },
      ...package_name ? { package_name } : {}
    };
    trackEvent(eventCategory.contactUs, eventAction.click, eventLabel.contactUs);
    this.props.sendContactUsForm({
      data: params,
      isRealtor: (this.props.usage === contactUsUsage.agent || this.props.usage === contactUsUsage.realtorPackages)
    });
  };

  findInvalidRequiredFields = () => {
    const { formData } = this.state;
    return requiredFields(this.props.usage).filter(item => !this.validateField(item, formData));
  };

  validateField = (name, formData) => {
    let fieldIsValid;

    switch (name) {
      case 'email': {
        fieldIsValid = emailRegexValidation(formData[name]);
        break;
      }
      default: fieldIsValid = !!formData[name];
    }

    return fieldIsValid;
  };

  fieldIsInvalid = name => this.state.invalidFields.indexOf(name) !== -1;

  scrollToInvalidField = (fieldName) => {
    scroller.scrollTo(fieldName, {
      duration: 500,
      offset: (this.props.isMobile) ? -47 : 0,
      smooth: true,
      containerSelector: (this.props.isMobile) ? '#app' : null,
      containerId: (this.props.isMobile) ? 'app' : null
    });
  };

  render() {
    let formTitle = this.props.translate('contactUsPage', 'formTitle');
    switch (this.props.usage) {
      case contactUsUsage.agent:
        formTitle = (
          <div className={styles.agentDescription}>
            <span className={styles.formTitle}>{this.props.translate('contactUsPage', 'advertiseHere')}</span>
          </div>
        );
        break;
      case contactUsUsage.realtorPackages:
        formTitle = (
          <h2>{this.props.translate('contactUsPage', 'placeOrder').toUpperCase()}</h2>
        );
        break;
      case contactUsUsage.projects:
        formTitle = this.props.translate('contactUsPage', 'formTitleProjects');
        break;
      default:
        break;
    }
    return (
      <div
        className={cx(
          'contact-us-form-container',
          styles.container,
          { [styles.contactUsRealtor]: this.props.isPageForRealtor }
        )}
      >
        <div className={styles.formTitle}>{formTitle}</div>
        <form ref={(contactUsForm) => { this.contactUsForm = contactUsForm; }}>
          <UserDetailsSection
            translate={this.props.translate('contactUsPage', 'userDetailsSection')}
            fieldIsInvalid={this.fieldIsInvalid}
            handleChange={this.handleChange}
            isMobile={this.props.isMobile}
            className="contactUsForm"
            isRtl={this.props.isRtl}
            usage={this.props.usage}
            isPageForRealtor={this.props.isPageForRealtor}
          />
          <MessageSection
            translate={this.props.translate('contactUsPage', 'messageSection')}
            locale={this.props.locale}
            handleChange={this.handleChange}
            fieldIsInvalid={this.fieldIsInvalid}
            fetchReCaptchaToken={this.fetchReCaptchaToken}
            className="contactUsForm"
            showPreloader={this.state.showPreloader}
            onSubmit={this.handleSubmit}
            isRtl={this.props.isRtl}
            usage={this.props.usage}
            error={get(this.props, 'contactUs.error')}
          />
        </form>
        { this.props.isPageForRealtor && (
          <div className={styles.contactPhone}>
            <a href={`tel:${ourContacts.phone}`} data-rel="external" >{this.props.translate('realtorInfoPage', 'callUs')} {ourContacts.phone}</a>
          </div>
        )}
      </div>
    );
  }
}

ContactUsFormContainer.propTypes = {
  translate: PropTypes.func,
  locale: PropTypes.string.isRequired,
  isMobile: PropTypes.bool,
  sendContactUsForm: PropTypes.func,
  openMessageModal: PropTypes.func,
  hideMessageModal: PropTypes.func,
  userData: PropTypes.object,
  contactUs: PropTypes.object,
  isMessageModalOpen: PropTypes.bool,
  usage: PropTypes.string,
  isRtl: PropTypes.bool,
  isNative: PropTypes.bool,
  isContactUsModalOpen: PropTypes.bool,
  hideContactUsModal: PropTypes.func,
  isPageForRealtor: PropTypes.bool
};

const mapStateToProps = state => ({
  translate: getLang(state.lang, 'static'),
  locale: state.lang.locale,
  isMobile: state.windowDimentions.isMobile,
  userData: state.userAuthentication.data,
  contactUs: state.contactUs,
  isMessageModalOpen: state.messageModal.isMessageModalOpen
});

export default connect(mapStateToProps, {
  sendContactUsForm,
  openMessageModal,
  hideMessageModal
})(ContactUsFormContainer);
